import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  HStack,
  Heading,
  Box,
  Image,
  Card,
  CardBody,
  Button,
  VStack,
  Input,
  Divider,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';

import singlePhaseImage from '../../../../assets/projects/single-phase-project.svg';
import multiplePhasesImage from '../../../../assets/projects/multiple-phases-project.svg';
import formTemplateImage from '../../../../assets/projects/form_template_list.svg';
import workflowImage from '../../../../assets/projects/workflow_list.svg';

import {
  useGetAllFormTemplatesQuery,
  useGetAllWorkflowsQuery,
} from '../../../../services';

export const NewWorkflowModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: (formId?: number, workflowId?: number) => void;
}) => {
  const [formTemplateId, setFormTemplateId] = useState<number>();
  const [workflowId, setWorkflowId] = useState<number>();
  const [searchWorkflow, setSearchWorkflow] = useState<string>();
  const [newWorkflowSource, setNewWorkflowSource] = useState<
    'scratch' | 'cloned'
  >();
  const debouncedSearchWorkflow = useDebounce(searchWorkflow, 300);

  const { projectId } = useParams();

  const { data: formTemplates } = useGetAllFormTemplatesQuery(
    {
      page: 0,
      size: 1000,
    },
    { skip: newWorkflowSource !== 'scratch' }
  );

  const { data: workflows } = useGetAllWorkflowsQuery(
    {
      page: 0,
      size: 1000,
      projectId,
      formName: debouncedSearchWorkflow,
    },
    {
      skip: newWorkflowSource !== 'cloned',
    }
  );

  return (
    <Modal
      blockScrollOnMount={false}
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
    >
      <ModalOverlay />

      <ModalContent pb="4">
        <ModalHeader>
          <Heading fontSize="md" fontWeight="semibold" color="custom_blue.1">
            {!newWorkflowSource
              ? 'Select What Will Be Created'
              : newWorkflowSource === 'scratch'
              ? 'Select Form'
              : 'Select To Create'}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!newWorkflowSource && (
            <HStack justify="space-between">
              <Box
                flexBasis="48%"
                onClick={() => setNewWorkflowSource('scratch')}
              >
                <Card
                  boxShadow="0 12px 16px -4px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)"
                  px="6"
                  py="7"
                >
                  <CardBody
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    color="custom_blue.1"
                    textAlign="center"
                  >
                    <Image src={singlePhaseImage} mb="4" />

                    <Box fontWeight="bold" fontSize="2xl" mb="4">
                      New Workflow
                    </Box>

                    <Box opacity=".7" fontSize="sm">
                      Before creating a workflow from scratch assign it to a
                      form.
                    </Box>
                  </CardBody>
                </Card>
              </Box>

              <Box
                flexBasis="48%"
                onClick={() => setNewWorkflowSource('cloned')}
              >
                <Card
                  boxShadow="0 12px 16px -4px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)"
                  px="6"
                  py="7"
                >
                  <CardBody
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    color="custom_blue.1"
                    textAlign="center"
                  >
                    <Image src={multiplePhasesImage} mb="4" />

                    <Box fontWeight="bold" fontSize="2xl" mb="4">
                      Clone Workflow
                    </Box>

                    <Box opacity=".7" fontSize="sm">
                      Use an existing workflow that is already created before
                    </Box>
                  </CardBody>
                </Card>
              </Box>
            </HStack>
          )}

          {newWorkflowSource === 'scratch' && (
            <VStack align="stretch" maxH="64" overflowY="scroll">
              {formTemplates?.data.content.map(({ id, name }) => (
                <HStack
                  key={id}
                  border="1px"
                  borderColor={
                    id === formTemplateId ? 'custom_orange.2' : 'custom_gray.4'
                  }
                  borderRadius="md"
                  p="2"
                  onClick={() => setFormTemplateId(id)}
                  cursor="pointer"
                >
                  <Image src={formTemplateImage} />
                  <Box fontWeight="medium" fontSize="md">
                    {name}
                  </Box>
                </HStack>
              ))}
            </VStack>
          )}

          {newWorkflowSource === 'cloned' && (
            <VStack align="stretch" maxH="64" overflowY="scroll">
              <Input
                placeholder="Search"
                onChange={e => setSearchWorkflow(e.target.value)}
              />

              {workflows?.data.content.map(({ id, name, formId, formName }) => (
                <HStack
                  key={id}
                  border="1px"
                  borderColor={
                    id === workflowId ? 'custom_orange.2' : 'custom_gray.4'
                  }
                  borderRadius="md"
                  p="2"
                  onClick={() => {
                    setWorkflowId(id);
                    setFormTemplateId(formId);
                  }}
                  cursor="pointer"
                >
                  <HStack flex="1">
                    <Image src={workflowImage} />
                    <Box fontWeight="medium" fontSize="md">
                      {name}
                    </Box>
                  </HStack>

                  <Divider orientation="vertical" h="8" />

                  <HStack flex="1">
                    <Image src={formTemplateImage} />
                    <Box fontWeight="medium" fontSize="md">
                      {formName}
                    </Box>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          )}

          {newWorkflowSource && (
            <Button
              bg="custom_orange.2"
              _hover={{ bg: 'custom_orange.2' }}
              color="white"
              onClick={() => onClose(formTemplateId, workflowId)}
              w="full"
              mt="6"
              isDisabled={
                (!formTemplates?.data.content.length &&
                  newWorkflowSource === 'scratch') ||
                (!workflows?.data.content.length &&
                  newWorkflowSource === 'cloned') ||
                !formTemplateId
              }
            >
              Create
            </Button>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
